import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { FilterAlt as FilterIcon } from '@mui/icons-material';
import {
  getBriefingPerformanceFilter,
  saveBriefingPerformanceFilter
} from '../../services/apiService';

const darkGreenColor = '#1D4434';

const toText = (list) => (Array.isArray(list) ? list.join('\n') : '');
const toList = (text) => [...new Set(String(text || '').split(/[\n,]/).map((item) => item.trim()).filter(Boolean))];
const sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Which traffic the Client Performance figures leave out, as set in Datadog queries: HighQ actions and extra
 * user-agent patterns. Health checks, static files and the built-in bot patterns always stay left out.
 * A change applies to months synced from now on; the Sync button above re-measures the months Datadog still holds.
 */
const BriefingPerformanceFilterAdmin = () => {
  const [state, setState] = useState(null);
  const [actionsText, setActionsText] = useState('');
  const [botsText, setBotsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getBriefingPerformanceFilter();
      if (!res?.success) throw new Error(res?.message || 'Could not read the filter');
      setState(res.data);
      setActionsText(toText(res.data.filter.excludedActions));
      setBotsText(toText(res.data.filter.extraBotPatterns));
    } catch (err) {
      setNotice({ severity: 'error', text: err?.message || 'Could not read the filter.' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actions = toList(actionsText);
  const bots = toList(botsText);
  const dirty = Boolean(state)
    && !(sameList(actions, state.filter.excludedActions) && sameList(bots, state.filter.extraBotPatterns));

  const handleSave = useCallback(async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await saveBriefingPerformanceFilter({ excludedActions: actions, extraBotPatterns: bots });
      if (!res?.success) throw new Error(res?.message || 'Could not save the filter');
      setState(res.data);
      setActionsText(toText(res.data.filter.excludedActions));
      setBotsText(toText(res.data.filter.extraBotPatterns));
      setNotice({
        severity: 'success',
        text: 'Saved. It applies to months synced from now on. Press Sync above to re-measure the last ~30 days; older months keep the filter they were measured with.'
      });
    } catch (err) {
      setNotice({ severity: 'error', text: err?.message || 'Could not save the filter.' });
    } finally {
      setSaving(false);
    }
  }, [actions, bots]);

  const handleReset = useCallback(() => {
    if (!state) return;
    setActionsText(toText(state.defaults.excludedActions));
    setBotsText(toText(state.defaults.extraBotPatterns));
  }, [state]);

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <FilterIcon sx={{ color: darkGreenColor }} />
        <Typography variant="h6" sx={{ color: darkGreenColor }}>
          Traffic left out of the figures
        </Typography>
        {state ? (
          <Chip
            size="small"
            label={state.isDefault ? 'Default filter' : `Custom filter (${state.fingerprint})`}
            sx={{ fontSize: 10, height: 18 }}
          />
        ) : null}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Health checks, static files and common bots are always left out. Add more below, one per line (letters, numbers, _ . and * only).
      </Typography>

      {!state && !notice ? <CircularProgress size={18} sx={{ color: darkGreenColor }} /> : null}

      {state ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <TextField
            label="Excluded actions"
            helperText="HighQ action names, e.g. getUploadInfo"
            multiline
            minRows={4}
            value={actionsText}
            onChange={(event) => setActionsText(event.target.value)}
            disabled={saving}
          />
          <TextField
            label="Extra bot patterns"
            helperText="User-agent patterns, e.g. *Zabbix*"
            multiline
            minRows={4}
            value={botsText}
            onChange={(event) => setBotsText(event.target.value)}
            disabled={saving}
          />
        </Box>
      ) : null}

      {state ? (
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={!dirty || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderColor: darkGreenColor, color: darkGreenColor, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving…' : 'Save filter'}
          </Button>
          <Button onClick={handleReset} disabled={saving} sx={{ textTransform: 'none', color: darkGreenColor }}>
            Reset to defaults
          </Button>
        </Box>
      ) : null}

      {notice ? (
        <Alert severity={notice.severity} sx={{ mt: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      ) : null}
    </Paper>
  );
};

export default BriefingPerformanceFilterAdmin;
